import { Capacitor } from '@capacitor/core';
import type { PushNotificationSchema } from '@capacitor/push-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import type { JSX } from 'react';
import { useEffect } from 'react';

import { navigatePushNotification } from 'pkg/actions/notifications';

import { flashes } from 'pkg/actions';
import * as endpoints from 'pkg/api/endpoints/auto';
import asyncLocalStorage from 'pkg/asyncLocalStorage';
import * as sdk from 'pkg/core/sdk';
import { Flag, useFlag } from 'pkg/flags';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import * as logger from 'pkg/logger';
import { isAndroid, isIOS } from 'pkg/platform';

import { supportsChatV2 } from 'routes/chat/use_chat_v2';

const hasPushNotifications = Capacitor.isPluginAvailable('PushNotifications');

/**
 *	What this build can do with a push, sent with the registration. `receipts`
 *	is a native build that acknowledges chat pushes itself, which on Android
 *	means it also draws every push, so the server sends it data-only pushes.
 *
 *	Claimed only where read receipts are on, with `chat_v2`, so turning the
 *	flag off puts phones back on ordinary pushes the next time they register,
 *	without an API deploy. And only from a native build that has the code: the
 *	native side registers the `ChatReceipts` plugin to say so. This file
 *	reaches older native builds over the air, and an Android build without
 *	that code would show no notifications at all, so it is never read from
 *	the web bundle's own version.
 *
 *	`channels` is an app that can open chat v2's channels: only a device that
 *	claims it is sent pushes about them, and only its badge counts them
 *	(PLA-718). It needs no native code, as a channel's push is an ordinary
 *	one, so it follows whether chat v2 runs here, not the build.
 */
export function pushCapabilities(
	receiptsAllowed: boolean,
	channelsAllowed: boolean = false
): string[] {
	const capabilities: string[] = [];

	if (receiptsAllowed && Capacitor.isPluginAvailable('ChatReceipts')) {
		capabilities.push('receipts');
	}

	if (channelsAllowed) {
		capabilities.push('channels');
	}

	return capabilities;
}

interface PushDevice {
	id: string;
	type: string;
}

/** This device's push token, once the plugin has handed it over. */
let pushDevice: PushDevice | null = null;
let receiptsAllowed = false;

/** Where this device remembers it has run chat v2, see `claimChannelPushes`. */
const ChannelsClaimedKey = 'pushCapabilities.channels';

function readChannelsClaimed(): boolean {
	try {
		return window.localStorage.getItem(ChannelsClaimedKey) === 'true';
	} catch {
		return false;
	}
}

let channelsAllowed = readChannelsClaimed();

function postPushDevice(): void {
	if (!pushDevice) {
		return;
	}

	sdk.post(
		endpoints.Users.RegisterPushNotificationDevice(),
		{},
		{
			...pushDevice,
			// A stored claim can be from an iPhone that ran v2 before v2 needed
			// the Popover API, and now runs v1, which can't open a channel (PLA-763).
			capabilities: pushCapabilities(
				receiptsAllowed,
				channelsAllowed && supportsChatV2()
			),
		}
	);
}

/** Registers this device's push token with the API. */
export function registerPushDevice(device: PushDevice): void {
	pushDevice = device;
	postPushDevice();
}

/**
 *	Whether this device may claim `receipts`, from `chat_v2` for the current
 *	organisation, which is only known inside the flag provider, after the
 *	device may already have registered. A change registers it again.
 */
export function allowReceiptPushes(allowed: boolean): void {
	if (allowed === receiptsAllowed) {
		return;
	}

	receiptsAllowed = allowed;
	postPushDevice();
}

/**
 *	Claims `channels` for this device, once chat v2 has run on it, which
 *	`ChannelObserver` says by mounting. The flag alone isn't enough, as a
 *	browser that can't draw v2 runs v1 with it on (`useChatV2`).
 *
 *	The claim is kept from then on, in another club and after a restart: the
 *	API pushes a channel only from a club with chat v2 on, and opening the
 *	push switches to that club, where v2 runs. Taking it back on the way into
 *	a club without v2 cost the account its channel pushes from the other
 *	club, and a club switch sent two registrations that could land in either
 *	order (PLA-726).
 */
export function claimChannelPushes(): void {
	if (channelsAllowed) {
		return;
	}

	channelsAllowed = true;

	try {
		window.localStorage.setItem(ChannelsClaimedKey, 'true');
	} catch {
		// Kept for this run only: the next start claims it again once v2 runs.
	}

	postPushDevice();
}

/** Keeps `allowReceiptPushes` in step with `chat_v2`. Mount inside the flag provider. */
export function ReceiptPushesObserver(): JSX.Element {
	const chatV2 = useFlag<boolean>(Flag.ChatV2);

	useEffect(() => {
		allowReceiptPushes(!!chatV2);
	}, [chatV2]);

	return null;
}

/**
 *	A push that arrived while the app was open, with the title and body a
 *	data-only push carries among its data where the flash looks for them.
 *	Only for arrivals: a tap on a drawn notification has them in its data
 *	too, and reads as before, without a flash.
 */
export function receivedPushNotification(
	notification: PushNotificationSchema
): PushNotificationSchema {
	return {
		...notification,
		title: notification.title || notification.data?.title,
		body: notification.body || notification.data?.body,
	};
}

const queuedPushNotificationActions = new Map<string, CallableFunction>();
let pushNotificationsRegistered = false;

export function isPushNotificationsRegistered(): boolean {
	return pushNotificationsRegistered;
}

/**
 * Holds an action that needs a registered device until the registration event
 * arrives. Actions are keyed and idempotent, so attempting the same one
 * repeatedly while unregistered runs it once on flush.
 */
export function queuePushNotificationAction(
	key: string,
	callable: CallableFunction
): void {
	queuedPushNotificationActions.set(key, callable);
}

/**
 * @NOTE Every action here returns a promise, so each has to be awaited inside
 * the catch — an unawaited call escapes as exactly the unhandled rejection
 * this queue exists to prevent, and these are the actions most likely to
 * reject since the device was unregistered when they were attempted. The
 * queue is drained before the first await so a second flush cannot run the
 * same action twice.
 */
async function callQueuedPushNotificationsActions(): Promise<void> {
	if (queuedPushNotificationActions.size === 0) {
		return;
	}

	const actions = Array.from(queuedPushNotificationActions.values());
	queuedPushNotificationActions.clear();

	for (const action of actions) {
		try {
			await action();
		} catch (error) {
			logger.error(
				error instanceof Error
					? error
					: new Error('Queued push notification action failed'),
				{ error }
			);
		}
	}
}

type PushNotificationCallback = (pns: PushNotificationSchema) => void;

let __handlePushNotification: PushNotificationCallback = () => null;

async function pushNotifications(): Promise<void> {
	if (!hasPushNotifications) {
		return;
	}

	try {
		let status = await PushNotifications.checkPermissions();
		if (status.receive === 'prompt') {
			status = await PushNotifications.requestPermissions();
		}

		// User has denied notification, no further setup is needed
		if (status.receive !== 'granted') {
			return;
		}

		// The listeners must be attached before register(), or the registration
		// event fires before anything is listening for it.
		PushNotifications.addListener('registration', (token) => {
			let registrationType = 'fcm';
			let tokenValue = token.value;

			if (isIOS()) {
				registrationType = 'apns';
				tokenValue = tokenValue.toLowerCase();
			}

			// Only now is the device actually registered. Plugin actions called
			// before this point reject on iOS with
			// "capacitorDidRegisterForRemoteNotifications not called".
			pushNotificationsRegistered = true;

			asyncLocalStorage.setItem('pnsID', tokenValue);

			registerPushDevice({ id: tokenValue, type: registrationType });

			void callQueuedPushNotificationsActions();
		});

		await PushNotifications.register();

		if (isAndroid()) {
			PushNotifications.createChannel({
				id: 'com.360player.app.default_notifications',
				name: 'General 360Player notifications',
				importance: 3,
				vibration: true,
				visibility: 1,
				sound: 'default',
			});
		}

		if (hasPushNotifications) {
			// Only triggers when the app is open
			PushNotifications.addListener(
				'pushNotificationReceived',
				(notification) => {
					__handlePushNotification(receivedPushNotification(notification));
				}
			);

			PushNotifications.addListener(
				'pushNotificationActionPerformed',
				(action) => {
					switch (action.actionId) {
						case 'tap':
							__handlePushNotification(action.notification);
							navigatePushNotification(action.notification.data);
							break;
					}
				}
			);
		}
	} catch (error) {
		// Firebase not initialized or other push notification error
		// (google-services.json may be missing in development)
		console.warn('Push notifications setup failed:', error);
		// App continues to work without push notifications
	}
}

export function usePushNotificationSender(): PushNotificationCallback {
	return (pns: PushNotificationSchema) => __handlePushNotification(pns);
}

/**
 *	Whether a push's link is the page on screen, so its flash would only
 *	repeat it: the same path, and the same query when the link carries one
 *	(an evaluation's `?run=`, a video's `?clip=`). A link without a query
 *	matches its path whatever the page's own query is.
 */
export function isCurrentPage(
	url: string | undefined,
	location: Pick<Location, 'origin' | 'pathname' | 'search'>
): boolean {
	if (!url) {
		return false;
	}

	const target = new URL(url, location.origin);

	if (target.pathname !== location.pathname) {
		return false;
	}

	return target.search === '' || target.search === location.search;
}

export function PushNotificationsObserver(): JSX.Element {
	const capturePushNotification = (notification: PushNotificationSchema) => {
		if (!notification.title && !notification.body) {
			return;
		}

		if (!isCurrentPage(notification.data?.url, window.location)) {
			flashes.show({
				title: notification.title,
				message: notification.body,
				additionalData: notification.data,
			});
		}

		return;
	};

	useComponentDidMount(async () => {
		__handlePushNotification = capturePushNotification;

		await pushNotifications();
	});

	return null;
}
